import React, {
	Component,
	type ReactElement,
	type MouseEvent,
	type ComponentPropsWithRef,
	type Ref,
} from 'react';
// eslint-disable-next-line jira/restricted/prop-types
import PropTypes from 'prop-types';
import { styled, css } from '@compiled/react';
import noop from 'lodash/noop';
// eslint-disable-next-line @atlaskit/design-system/no-unsupported-drag-and-drop-libraries
import {
	Draggable,
	type DraggableProvidedDragHandleProps,
	type DraggableStateSnapshot,
} from 'react-beautiful-dnd';
import { noFocusRing } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';

// TODO: migrate to object syntax. Autofix is available for many cases. Remove the eslint-disable for @atlaskit/design-system/no-compiled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled to check.
// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CardContainer = styled.li`
	margin: 0;
	&:last-child > div {
		${noFocusRing};
		border-bottom-color: transparent;
	}
`;
// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DraggableCardContainer = styled.li`
	&:last-child > div {
		${noFocusRing};
		border-bottom-color: transparent;
	}
`;

// eslint-disable-next-line @atlaskit/design-system/no-css-tagged-template-expression
const clearBorderMixin = css`
	&:last-child {
		${noFocusRing};
		border-bottom-color: transparent;
	}
`;

const withFixedHeight = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${gridSize * 5}px`,
});

type CardPropsShape = {
	isDragging?: boolean;
	isInteractive?: boolean;
	isVariableHeight?: boolean;
	isDraggable?: boolean;
	isHovering?: boolean;
	isActive?: boolean;
};

type CardProps = Omit<ComponentPropsWithRef<typeof CardNew>, 'ref'> & { ref?: Ref<HTMLDivElement> };

// TODO: migrate to object syntax. Autofix is available for many cases. Remove the eslint-disable for @atlaskit/design-system/no-compiled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled to check.
// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CardOld = styled.div<CardPropsShape>`
	background: ${token('color.background.neutral.subtle')};
	border-radius: '4px';
	cursor: ${({ isDragging, isInteractive }) => {
		if (isInteractive) {
			return isDragging ? 'grabbing' : 'pointer';
		}
		return 'auto';
	}};
	display: flex;
	position: relative;
	${({ isVariableHeight }) => (!isVariableHeight ? withFixedHeight : css({}))}
	padding: 0 ${token('space.075')};
	margin: 0 ${token('space.025')};

	border-bottom: 1px solid
		${({ isDragging }) =>
			isDragging ? token('utility.UNSAFE.transparent') : token('color.border')};

	${({ isDraggable }) => (!isDraggable ? clearBorderMixin : css({}))}
	${({ isHovering }) =>
		isHovering
			? css({
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					backgroundColor: token('color.background.neutral.subtle.hovered'),
					textDecoration: 'none',
				})
			: css({})}

	${({ isActive, isDragging }) =>
		isActive
			? css({
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
					background: isDragging
						? token('elevation.surface.overlay')
						: // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
							token('color.background.neutral.subtle.pressed'),
					boxShadow: token('elevation.shadow.overlay'),
				})
			: css({})}

	&:focus {
		z-index: 1;
		outline: none;
		box-shadow: ${token('color.border.focused')} 0 0 0 2px;
	}
`;

// TODO: This can be  removed only once Compiled components are removed from ItemLineCard. That would entail rewiting the entire logic of how styling is currently achieved for each card.
// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled
const CardNew = styled.div<CardPropsShape>`
	background: ${token('color.background.neutral.subtle')};
	border-radius: '4px';
	cursor: ${({ isDragging, isInteractive }) => {
		if (isInteractive) {
			return isDragging ? 'grabbing' : 'pointer';
		}
		return 'auto';
	}};
	display: flex;
	position: relative;
	${({ isVariableHeight }) => (!isVariableHeight ? withFixedHeight : css({}))}
	padding: 0 ${token('space.075')};
	margin: 0 ${token('space.025')};

	border-bottom: 1px solid
		${({ isDragging }) =>
			isDragging ? token('utility.UNSAFE.transparent') : token('color.border')};

	${({ isHovering }) =>
		isHovering
			? css({
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					backgroundColor: token('color.background.neutral.subtle.hovered'),
					textDecoration: 'none',
				})
			: css({})}

	${({ isActive, isDragging }) =>
		isActive
			? css({
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
					background: isDragging
						? token('elevation.surface.overlay')
						: // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
							token('color.background.neutral.subtle.pressed'),
					boxShadow: token('elevation.shadow.overlay'),
				})
			: css({})}

	&:focus {
		z-index: 1;
		outline: none;
		box-shadow: ${token('color.border.focused')} 0 0 0 2px;
	}
`;

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
const Card = componentWithFG(
	'issue-view_item-line-card-a11y-clone',
	CardNew,
	CardOld,
) as unknown as typeof CardNew;

const isMiddleClick = (event: MouseEvent<HTMLElement> | MouseEvent<HTMLElement, MouseEvent>) =>
	event.button === 1;

export type Item = {
	readonly id: string;
};

export type ItemRenderer<T> = (
	isHovering: boolean,
	isActive: boolean,
	isFocused: boolean,
	item: T,
) => ReactElement;

export type ItemLineCardProps<T> = {
	item: T;
	index: number;
	isReorderEnabled: boolean;
	isInteractive: boolean;
	isVariableHeight: boolean;
	children: ItemRenderer<T>;
	onClick: (item: T, arg2: MouseEvent<HTMLElement>) => void;
};

type State = {
	isHovering: boolean;
	isActive: boolean;
	isFocused: boolean;
};

type EventHandlers = {
	onBlur: () => void;
	onClick: (arg1: MouseEvent<HTMLElement>) => void;
	onDragEnd: () => void;
	onFocus: () => void;
	onMouseEnter: () => void;
	onMouseLeave: (arg1: MouseEvent<HTMLElement>) => void;
	onMouseDown: (arg1: MouseEvent<HTMLElement>) => void;
	onMouseUp: () => void;
};

// eslint-disable-next-line jira/react/no-class-components
export default class ItemLineCard<T extends Item> extends Component<ItemLineCardProps<T>, State> {
	static propTypes? = {
		item: PropTypes.object.isRequired,
		index: PropTypes.number.isRequired,
		isReorderEnabled: PropTypes.bool,
		isInteractive: PropTypes.bool,
		isVariableHeight: PropTypes.bool,
		children: PropTypes.func.isRequired,
		onClick: PropTypes.func,
	};

	static defaultProps = {
		isReorderEnabled: false,
		isInteractive: true,
		isVariableHeight: false,
		onClick: noop,
	};

	state = {
		isHovering: false,
		isActive: false,
		isFocused: false,
	};

	eventHandlers: EventHandlers = {
		onBlur: () => {
			this.setState({ isFocused: false });
		},
		onClick: (event: MouseEvent<HTMLElement>) => {
			// best practice to use for react-beautiful-dnd (https://github.com/atlassian/react-beautiful-dnd/blob/master/docs/guides/how-we-use-dom-events.md)
			if (event.defaultPrevented === true) {
				return;
			}

			// Middle clicks are handled in onMouseDown for cross browser support.
			if (!isMiddleClick(event)) {
				this.propagateClick(event);
			}
		},
		onDragEnd: () => this.setState({ isActive: false }),
		onFocus: () => {
			this.setState({ isFocused: true });
		},
		onMouseEnter: () => this.setState({ isHovering: true }),
		onMouseLeave: () => this.setState({ isHovering: false, isActive: false }),
		onMouseDown: (event: MouseEvent<HTMLElement>) => {
			if (isMiddleClick(event)) {
				this.propagateClick(event);
			}
			this.setState({ isActive: true });
		},
		onMouseUp: () => this.setState({ isActive: false }),
	};

	propagateClick = (event: MouseEvent<HTMLElement>) => {
		this.props.onClick(this.props.item, event);
	};

	patchedHandlers = (
		dragHandleProps: DraggableProvidedDragHandleProps | null | undefined,
		snapshot: DraggableStateSnapshot,
	) => {
		/* The 'isActive' state is determined by the draggable state,
           i.e. if isDragging then the state is considered active. */
		const onMouseDown = (event: MouseEvent<HTMLElement>) => {
			if (isMiddleClick(event)) {
				this.propagateClick(event);
			}
		};

		const onClick = !dragHandleProps
			? this.eventHandlers.onClick
			: (event: MouseEvent<HTMLElement>) => {
					if (!snapshot.isDragging) {
						this.eventHandlers.onClick(event);
					}
				};

		return {
			...dragHandleProps,
			...this.eventHandlers,
			onClick,
			onMouseDown,
		};
	};

	renderCard = (cardProps: CardProps) => {
		const { isInteractive, item, isVariableHeight } = this.props;
		const { isHovering, isFocused } = this.state;
		const isActive = Boolean(cardProps.isDragging) || this.state.isActive;
		return (
			<Card
				{...cardProps}
				{...this.state}
				isInteractive={isInteractive}
				isActive={isActive}
				isVariableHeight={isVariableHeight}
			>
				{this.props.children(isHovering, isActive, isFocused, item)}
			</Card>
		);
	};

	renderDraggableCard() {
		return (
			<Draggable draggableId={this.props.item.id} index={this.props.index}>
				{(provided, snapshot) => {
					const cardJSX = this.renderCard({
						ref: provided.innerRef,
						isDraggable: true,
						isDragging: snapshot.isDragging,
						...provided.draggableProps,
						...this.patchedHandlers(provided.dragHandleProps, snapshot),
					});
					return fg('issue-view_item-line-card-a11y-clone') ? (
						<CardContainer>{cardJSX}</CardContainer>
					) : (
						<DraggableCardContainer>{cardJSX}</DraggableCardContainer>
					);
				}}
			</Draggable>
		);
	}

	render() {
		if (this.props.isReorderEnabled) {
			return this.renderDraggableCard();
		}

		if (fg('issue-view_item-line-card-a11y-clone')) {
			return (
				<CardContainer>
					{this.renderCard({
						...this.eventHandlers,
					})}
				</CardContainer>
			);
		}
		return this.renderCard({
			...this.eventHandlers,
		});
	}
}
